/**
 * makeSlider(trackEl, arrowBtns) → { reset }
 *
 * Reusable horizontal slider with:
 *  - Arrow button navigation
 *  - Pointer-based drag/swipe (mouse + touch + stylus via Pointer Events API)
 *  - Returns reset() to jump back to index 0 (used by featured category filter)
 *
 * Gap is read from computed styles, so each block controls it via CSS.
 */
function makeSlider(trackEl, arrowBtns) {
    if (!trackEl) return { reset: function () {} };

    var index     = 0;
    var itemW     = 0;
    var gap       = 0;
    var visCount  = 0;
    var THRESHOLD = 50; // px drag distance to trigger a slide

    // ── Visible items (category filter hides some) ────────────────────────────

    function visibleItems() {
        return Array.from(trackEl.children).filter(function (el) {
            return el.style.display !== 'none';
        });
    }

    // ── Measurements ──────────────────────────────────────────────────────────

    function measure() {
        var items = visibleItems();
        if (!items.length) return;
        itemW    = items[0].getBoundingClientRect().width;
        gap      = parseInt(getComputedStyle(trackEl).gap) || 20;
        var wrapW = trackEl.parentElement.getBoundingClientRect().width;
        visCount  = Math.max(1, Math.floor((wrapW + gap) / (itemW + gap)));
        clamp(items.length);
    }

    function clamp(total) {
        var count = typeof total === 'number' ? total : visibleItems().length;
        var max   = Math.max(0, count - visCount);
        if (index > max) index = max;
        if (index < 0)   index = 0;
    }

    // ── Render position ───────────────────────────────────────────────────────

    function update() {
        trackEl.style.transform = 'translateX(-' + (index * (itemW + gap)) + 'px)';
        if (!arrowBtns) return;
        var total = visibleItems().length;
        arrowBtns.forEach(function (btn) {
            var dir = parseInt(btn.getAttribute('data-dir'));
            btn.disabled = (dir < 0 && index === 0) ||
                           (dir > 0 && index >= total - visCount);
        });
    }

    // ── Arrow buttons ─────────────────────────────────────────────────────────

    if (arrowBtns) {
        arrowBtns.forEach(function (btn) {
            btn.addEventListener('click', function () {
                index += parseInt(btn.getAttribute('data-dir'));
                clamp();
                update();
            });
        });
    }

    // ── Pointer drag / swipe ──────────────────────────────────────────────────

    var dragStartX = null;
    var dragging   = false;
    var liveOffset = 0; // live translate during drag

    trackEl.addEventListener('pointerdown', function (e) {
        if (e.button !== 0 && e.pointerType === 'mouse') return; // left-click only for mouse
        dragStartX = e.clientX;
        dragging   = false;
        liveOffset = 0;
        trackEl.setPointerCapture(e.pointerId);
        trackEl.classList.add('is-dragging');
    });

    trackEl.addEventListener('pointermove', function (e) {
        if (dragStartX === null) return;
        var delta = e.clientX - dragStartX;
        if (Math.abs(delta) > 5) dragging = true;
        if (!dragging) return;
        // Live feedback: show the drag offset on top of the current position
        liveOffset = delta;
        trackEl.style.transform = 'translateX(' + (-(index * (itemW + gap)) - delta * -1) + 'px)';
    });

    trackEl.addEventListener('pointerup', function (e) {
        if (dragStartX === null) return;
        var delta = dragStartX - e.clientX; // positive = dragged left (next)
        dragStartX = null;
        trackEl.classList.remove('is-dragging');

        if (Math.abs(delta) >= THRESHOLD) {
            index += delta > 0 ? 1 : -1;
            clamp();
        }
        update(); // snap to correct position (also clears live offset)
    });

    trackEl.addEventListener('pointercancel', function () {
        dragStartX = null;
        dragging   = false;
        trackEl.classList.remove('is-dragging');
        update(); // snap back
    });

    // Prevent link navigation while dragging
    trackEl.addEventListener('click', function (e) {
        if (dragging) {
            e.preventDefault();
            dragging = false;
        }
    }, true);

    // ── Init ──────────────────────────────────────────────────────────────────

    measure();
    update();
    window.addEventListener('resize', function () { measure(); update(); });

    return {
        reset: function () {
            index = 0;
            measure();
            update();
        },
    };
}
